import React from "react";
import { Link } from "react-router-dom";
import { Dropdown, Tag, Tooltip, MenuProps } from "antd";
import type { Job } from "../../../../core/data/redux/recruitment/types";
import { humanReadableDate } from "../../../../helper/dateTimeHelper";
import { notify } from "../../../../services/toast.config";

interface JobCardProps {
  job: Job;
  onEdit: (job: Job) => void;
  onClone: (job: Job) => void;
  onDelete: (jobId: number) => void;
  onPublish: (jobId: number) => void;
  onChangeStatus: (jobId: number, status: string) => void;
  onCloseJob: (job: Job) => void;
  onUnpublish: (jobId: number, platform: string) => void;
  departmentMap: Record<number, string>;
}

const STATUS_CONFIG: Record<string, { bg: string; color: string; dot: string }> = {
  active: { bg: "#ecfdf5", color: "#059669", dot: "#10b981" },
  closed: { bg: "#fef2f2", color: "#dc2626", dot: "#ef4444" },
  draft: { bg: "#f1f5f9", color: "#64748b", dot: "#94a3b8" },
};

const GRADIENT: Record<string, string> = {
  active: "linear-gradient(135deg, #10b981 0%, #34d399 100%)",
  closed: "linear-gradient(135deg, #ef4444 0%, #f87171 100%)",
  draft: "linear-gradient(135deg, #94a3b8 0%, #cbd5e1 100%)",
};

const JobCard: React.FC<JobCardProps> = ({
  job,
  onEdit,
  onClone,
  onDelete,
  onPublish,
  onChangeStatus,
  onCloseJob,
  onUnpublish,
  departmentMap,
}) => {
  const sc = STATUS_CONFIG[job.status] || STATUS_CONFIG.draft;

  const posted = humanReadableDate(job.created_at);

  const getActionItems = (): MenuProps["items"] => {
    const items: MenuProps["items"] = [
      {
        key: "edit",
        icon: <i className="ti ti-edit" style={{ color: "#3b82f6" }} />,
        label: "Edit Job",
        onClick: () => onEdit(job),
      },
      {
        key: "clone",
        icon: <i className="ti ti-copy" style={{ color: "#8b5cf6" }} />,
        label: "Create Similar",
        onClick: () => onClone(job),
      },
      {
        key: "publish",
        icon: <i className="ti ti-share" style={{ color: "#10b981" }} />,
        label: "Publish Options",
        onClick: () => onPublish(job.id),
      },
      {
        key: "copy-url",
        icon: <i className="ti ti-link" style={{ color: "#0ea5e9" }} />,
        label: job.job_share_url ? "Copy Share URL" : "Share URL (page offline)",
        disabled: !job.job_share_url,
        onClick: () => {
          navigator.clipboard.writeText(job.job_share_url!);
          notify.success("Job URL copied to clipboard!");
        },
      },
      { type: "divider" },
      {
        key: "status-group",
        type: "group",
        label: "Change Status",
        children: [
          {
            key: "status-active",
            label: <span className={job.status === "active" ? "fw-bold text-success" : ""}>Active</span>,
            disabled: job.status === "active",
            onClick: () => onChangeStatus(job.id, "active"),
          },
          {
            key: "status-draft",
            label: <span className={job.status === "draft" ? "fw-bold text-warning" : ""}>Draft</span>,
            disabled: job.status === "draft",
            onClick: () => onChangeStatus(job.id, "draft"),
          },
        ]
      },
      { type: "divider" },
      {
        key: "close",
        icon: <i className="ti ti-lock" style={{ color: "#ef4444" }} />,
        label: "Close Job",
        disabled: job.status === "closed",
        onClick: () => onCloseJob(job),
      },
    ];

    if (job.platforms && job.platforms.length > 0) {
      const publishedOn = job.platforms.filter((p) => p.status === "published");
      if (publishedOn.length > 0) {
        items.push({ type: "divider" });
        items.push({
          key: "unpublish-group",
          type: "group",
          label: "Unpublish From",
          children: publishedOn.map((p) => ({
            key: `unpublish-${p.platform}`,
            label: <span className="text-danger">{(p.platform === "career_page" ? "Career Page" : p.platform.charAt(0).toUpperCase() + p.platform.slice(1))}</span>,
            onClick: () => onUnpublish(job.id, p.platform),
          }))
        });
      }
    }

    items.push({ type: "divider" });
    items.push({
      key: "delete",
      icon: <i className="ti ti-trash" />,
      danger: true,
      label: "Delete",
      onClick: () => onDelete(job.id),
    });

    return items;
  };

  return (
    <div
      className="card h-100 border-0 overflow-hidden slide-up-enter"
      style={{
        borderRadius: 14,
        boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
        transition: "all 0.25s ease",
        cursor: "pointer",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-4px)";
        e.currentTarget.style.boxShadow = "0 12px 28px rgba(0,0,0,0.10)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "0 1px 8px rgba(0,0,0,0.06)";
      }}
    >
      
      <div style={{ height: 5, background: GRADIENT[job.status] || GRADIENT.draft }} />

      <div className="card-body d-flex flex-column" style={{ padding: "14px 16px 12px" }}>
        
        <div className="d-flex justify-content-between align-items-center mb-2">
          <Tooltip title={`Status: ${job.status}`}>
            <span
              className="d-inline-flex align-items-center gap-1 cursor-help"
              style={{
                background: sc.bg,
                color: sc.color,
                fontSize: 11,
                fontWeight: 600,
                padding: "3px 10px",
                borderRadius: 20,
                textTransform: "capitalize",
              }}
            >
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: sc.dot, display: "inline-block" }} />
              {job.status}
            </span>
          </Tooltip>
          <Dropdown menu={{ items: getActionItems() }} trigger={["click"]} placement="bottomRight">
            <button
              className="btn p-0"
              style={{ background: "none", border: "none", lineHeight: 1 }}
              onClick={(e) => e.stopPropagation()}
            >
              <Tooltip title="Job Actions">
                <i className="ti ti-dots-vertical" style={{ fontSize: 18, color: "#94a3b8" }} />
              </Tooltip>
            </button>
          </Dropdown>
        </div>

        
        <Tooltip title={job.job_title}>
          <h6
            className="mb-1 text-truncate"
            style={{ fontSize: 15, fontWeight: 650, color: "#0f172a", letterSpacing: -0.2 }}
          >
            {job.job_title}
          </h6>
        </Tooltip>

        
        <Tooltip title={`Department: ${job?.department_id && departmentMap[job.department_id] || "—"}`}>
          <p className="mb-2 text-truncate" style={{ fontSize: 12.5, color: "#64748b" }}>
            <i className="ti ti-building me-1" style={{ fontSize: 13 }} />
            {job?.department_id && departmentMap[job.department_id] || "—"}
          </p>
        </Tooltip>

        
        <div className="d-flex flex-wrap gap-1 mb-2">
          <Tooltip title={`Location: ${job.work_location || "—"}`}>
            <span
              className="d-inline-flex align-items-center cursor-help"
              style={{ background: "#f1f5f9", padding: "3px 8px", borderRadius: 6, fontSize: 11, color: "#475569", fontWeight: 500 }}
            >
              <i className="ti ti-map-pin me-1" style={{ fontSize: 12 }} />
              {job.work_location || "—"}
            </span>
          </Tooltip>

          <Tooltip title={`Type: ${(job.employment_type || "").replace(/_/g, " ")}`}>
            <span
              className="d-inline-flex align-items-center cursor-help"
              style={{ background: "#eff6ff", padding: "3px 8px", borderRadius: 6, fontSize: 11, color: "#2563eb", fontWeight: 500 }}
            >
              <i className="ti ti-briefcase me-1" style={{ fontSize: 12 }} />
              {(job.employment_type || "").replace(/_/g, " ")}
            </span>
          </Tooltip>

          {job.work_mode && (
            <Tooltip title={`Work Mode: ${job.work_mode}`}>
              <span
                className="d-inline-flex align-items-center cursor-help"
                style={{ background: "#faf5ff", padding: "3px 8px", borderRadius: 6, fontSize: 11, color: "#7c3aed", fontWeight: 500 }}
              >
                <i className="ti ti-device-laptop me-1" style={{ fontSize: 12 }} />
                {job.work_mode}
              </span>
            </Tooltip>
          )}
        </div>

        
        <div className="d-flex gap-3 mb-2" style={{ fontSize: 12, color: "#64748b" }}>
          <Tooltip title={`Experience Level: ${job.min_experience_years}–${job.max_experience_years} years`}>
            <span className="cursor-help">
              <i className="ti ti-star me-1" style={{ fontSize: 13 }} />
              {job.min_experience_years}–{job.max_experience_years} yrs
            </span>
          </Tooltip>
          {job.openings_count != null && (
            <Tooltip title={`Openings: ${job.openings_count}`}>
              <span className="cursor-help">
                <i className="ti ti-users me-1" style={{ fontSize: 13 }} />
                {job.openings_count} {job.openings_count === 1 ? "opening" : "openings"}
              </span>
            </Tooltip>
          )}
        </div>

        
        {job.min_salary != null && job.max_salary != null && (
          <Tooltip title="Estimated Salary Range">
            <div
              className="mb-2 cursor-help"
              style={{
                background: "linear-gradient(90deg, #f0fdf4, #ecfdf5)",
                borderRadius: 8,
                padding: "5px 10px",
                fontSize: 12.5,
                fontWeight: 600,
                color: "#16a34a",
                border: "1px solid #bbf7d0",
              }}
            >
              <i className="ti ti-currency-rupee me-1" />
              {job.min_salary.toLocaleString("en-IN")} – {job.max_salary.toLocaleString("en-IN")}
            </div>
          </Tooltip>
        )}

        
        {job.platforms && job.platforms.length > 0 && (
          <div className="d-flex gap-1 mt-2 pt-1 flex-wrap" style={{ borderTop: "1px solid #f8fafc" }}>
            {job.platforms.map((p) => (
              <Tooltip key={p.platform} title={`Published on ${p.platform === "career_page" ? "Career Page" : p.platform.charAt(0).toUpperCase() + p.platform.slice(1)} (${p.status})`}>
                <Tag
                  color={p.status === "published" ? "green" : p.status === "pending" ? "orange" : "red"}
                  style={{ fontSize: 10, margin: "0 4px 4px 0", lineHeight: "16px", cursor: "help" }}
                >
                  {p.platform === "career_page" ? "Career" : p.platform.charAt(0).toUpperCase() + p.platform.slice(1)}
                </Tag>
              </Tooltip>
            ))}
          </div>
        )}

        
        <div
          className="d-flex align-items-center justify-content-between mt-auto pt-2"
          style={{ borderTop: "1px solid #f1f5f9" }}
        >
          <Tooltip title={`Posted Date: ${humanReadableDate(job.created_at)}`}>
            <span className="cursor-help" style={{ fontSize: 11.5, color: "#94a3b8" }}>
              <i className="ti ti-calendar me-1" />
              {posted}
            </span>
          </Tooltip>
          <Tooltip title={`Total Applications: ${job.application_count || 0}`}>
            <span
              className="d-flex align-items-center cursor-help"
              style={{ fontSize: 12.5, fontWeight: 600, color: "#3b82f6", gap: 4 }}
            >
              <i className="ti ti-users" style={{ fontSize: 14 }} />
              {job.application_count || 0} Applicants
            </span>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

export default JobCard;
